import { apiRequest } from "../../../shared/lib/api";
import {
  ChallengeStatus,
  ChallengeType,
  CreateChallengePayload,
  ChallengeDetail,
  ChallengeHistoryItem,
  UpdateChallengePayload,
} from "../types/challengeAdmin";

type ChallengeFilters = {
  name?: string;
  status?: ChallengeStatus | "all";
  types?: ChallengeType[];
  official?: boolean | "all";
  organizerUserId?: number;
  dateFrom?: string;
  dateTo?: string;
};

function buildQuery(filters?: ChallengeFilters) {
  const params = new URLSearchParams();
  if (!filters) return "";
  if (filters.name) params.set("name", filters.name);
  if (filters.status && filters.status !== "all") {
    params.set("status", filters.status);
  }
  if (filters.types && filters.types.length > 0) {
    params.set("type", filters.types.join(","));
  }
  if (typeof filters.official === "boolean") {
    params.set("official", filters.official ? "true" : "false");
  }
  if (Number.isFinite(filters.organizerUserId)) {
    params.set("organizerUserId", String(filters.organizerUserId));
  }
  if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.set("dateTo", filters.dateTo);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function listChallengeHistory(token: string, filters?: ChallengeFilters) {
  return apiRequest<ChallengeHistoryItem[]>(
    `/api/admin/tournaments${buildQuery(filters)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export function createChallenge(token: string, payload: CreateChallengePayload) {
  return apiRequest<ChallengeDetail>("/api/admin/tournaments", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export function getChallengeById(token: string, id: number) {
  return apiRequest<ChallengeDetail>(`/api/admin/tournaments/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function updateChallengeById(
  token: string,
  id: number,
  payload: UpdateChallengePayload
) {
  return apiRequest<ChallengeDetail>(`/api/admin/tournaments/${id}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export function syncChallengeEvent(
  token: string,
  id: number,
  payload: {
    enabled: boolean;
    imageUrl?: string;
    amountPerPlayerCents?: number;
    registrationType?: "individual" | "fixed_pair";
    registrationsOpen?: boolean;
    shirtSizesEnabled?: boolean;
    shirtSizes?: string[];
    shirtAvailabilityType?: "none" | "period" | "quantity";
    shirtDeadlineAt?: string | null;
    shirtQuantityLimit?: number | null;
  }
) {
  return apiRequest<{
    ok: boolean;
    enabled: boolean;
    eventId?: string;
    amountPerPlayerCents?: number;
    paymentMode?: string;
    registrationType?: "individual" | "fixed_pair";
    registrationsOpen?: boolean;
    shirtSizesEnabled?: boolean;
    shirtSizes?: string[];
    shirtAvailabilityType?: "none" | "period" | "quantity";
    shirtDeadlineAt?: string | null;
    shirtQuantityLimit?: number | null;
    shirtConfirmedCount?: number;
    shirtAvailableCount?: number | null;
  }>(`/api/admin/tournaments/${id}/event-sync`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export function deleteChallengeById(token: string, id: number) {
  return apiRequest<{ ok: boolean }>(`/api/admin/tournaments/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function listPublicChallenges(filters?: ChallengeFilters, token?: string) {
  const authHeader =
    token && token.trim() ? { Authorization: `Bearer ${token.trim()}` } : {};
  return apiRequest<ChallengeHistoryItem[]>(`/api/challenges${buildQuery(filters)}`, {
    headers: authHeader,
  });
}

export function getPublicChallengeById(id: number) {
  return apiRequest<ChallengeDetail>(`/api/challenges/${id}`);
}

export function getChallengeByIdMaybeAuth(id: number, token?: string) {
  const authHeader =
    token && token.trim() ? { Authorization: `Bearer ${token.trim()}` } : {};
  return apiRequest<ChallengeDetail>(`/api/challenges/${id}`, {
    headers: authHeader,
  });
}

export type PublicTournamentMatchSetScore = {
  matchId?: number;
  setNumber: number;
  gamesA?: number | null;
  gamesB?: number | null;
  tieBreakA?: number | null;
  tieBreakB?: number | null;
  isMatchTieBreak?: boolean;
  winnerSide?: "A" | "B" | null;
};

export type PublicTournamentBracketMatch = {
  id: number;
  stage?: "group" | "knockout" | string;
  groupKey?: string | null;
  groupName?: string | null;
  roundNumber: number;
  roundCode: string;
  matchNumber: number;
  sideARegistrationId?: number | null;
  sideBRegistrationId?: number | null;
  sideALabel?: string | null;
  sideBLabel?: string | null;
  sideAPlayerAvatarUrl?: string | null;
  sideAPartnerAvatarUrl?: string | null;
  sideAPlayerPublicPlayerId?: number | null;
  sideAPartnerPublicPlayerId?: number | null;
  sideAPlayerSex?: "male" | "female" | "other" | null;
  sideAPartnerSex?: "male" | "female" | "other" | null;
  sideBPlayerAvatarUrl?: string | null;
  sideBPartnerAvatarUrl?: string | null;
  sideBPlayerPublicPlayerId?: number | null;
  sideBPartnerPublicPlayerId?: number | null;
  sideBPlayerSex?: "male" | "female" | "other" | null;
  sideBPartnerSex?: "male" | "female" | "other" | null;
  sideAIsBye?: boolean;
  sideBIsBye?: boolean;
  winnerSide?: "A" | "B" | null;
  winnerRegistrationId?: number | null;
  decidedBy?: string;
  status: string;
  sourceMatchAId?: number | null;
  sourceMatchBId?: number | null;
  notes?: string | null;
  sets: PublicTournamentMatchSetScore[];
};

export type PublicTournamentBracketRound = {
  roundNumber: number;
  roundCode: string;
  matches: PublicTournamentBracketMatch[];
};

export type PublicTournamentBracket = {
  id: number;
  tournamentId: number;
  categoryKey: string;
  status: string;
  seedMode: string;
  participantCount: number;
  bracketSize: number;
  generatedAt?: string | null;
  publishedAt?: string | null;
  metadata?: {
    categoryName?: string;
    byeCount?: number;
    format?: string;
    groups?: Array<{
      id: string;
      name: string;
      size: number;
      isSeedHead?: boolean;
      teams: Array<{
        registrationId: number;
        label: string;
        playerAvatarUrl?: string | null;
        partnerAvatarUrl?: string | null;
        seedOrder?: number;
        seedRank?: number | null;
        isSeedHead?: boolean;
        hasRanking?: boolean;
        seedPoints?: number;
      }>;
    }>;
    qualifiedTeams?: Array<{
      label: string;
      groupId: string;
      groupName: string;
      groupPosition: number;
      generalSeed: number;
    }>;
  };
  matchSettings?: {
    bestOfSets?: number | null;
    finalBestOfSets?: number | null;
  } | null;
  rounds: PublicTournamentBracketRound[];
};

export type PublicTournamentScheduleSlot = {
  id: number;
  tournamentId: number;
  categoryKey?: string | null;
  slotStartAt: string;
  slotEndAt: string;
  actualStartedAt?: string | null;
  actualFinishedAt?: string | null;
  courtName: string;
  matchId?: number | null;
  status: string;
  lockReason?: string | null;
  roundNumber?: number | null;
  roundCode?: string | null;
  stage?: "group" | "knockout" | string | null;
  groupKey?: string | null;
  groupName?: string | null;
  matchNumber?: number | null;
  sideARegistrationId?: number | null;
  sideBRegistrationId?: number | null;
  sideALabel?: string | null;
  sideBLabel?: string | null;
  sideAPlayerAvatarUrl?: string | null;
  sideAPartnerAvatarUrl?: string | null;
  sideAPlayerPublicPlayerId?: number | null;
  sideAPartnerPublicPlayerId?: number | null;
  sideAPlayerSex?: "male" | "female" | "other" | null;
  sideAPartnerSex?: "male" | "female" | "other" | null;
  sideBPlayerAvatarUrl?: string | null;
  sideBPartnerAvatarUrl?: string | null;
  sideBPlayerPublicPlayerId?: number | null;
  sideBPartnerPublicPlayerId?: number | null;
  sideBPlayerSex?: "male" | "female" | "other" | null;
  sideBPartnerSex?: "male" | "female" | "other" | null;
  winnerSide?: "A" | "B" | null;
  matchStatus?: string | null;
  sets?: PublicTournamentMatchSetScore[];
};

export type PublicTournamentSchedule = {
  tournamentId: number;
  config?: {
    tournamentId?: number;
    dayStartAt: string;
    dayEndAt: string;
    courtNames: string[];
    defaultMatchDurationMinutes: number;
    roundPolicy?: string;
    updatedAt?: string | null;
  } | null;
  slots: PublicTournamentScheduleSlot[];
};

export type TournamentEventScheduleActivityType =
  | "games"
  | "opening"
  | "checkin"
  | "food"
  | "coffee"
  | "cake"
  | "exercise"
  | "draw"
  | "awards"
  | "celebration"
  | "break"
  | "general"
  | "other";

export type TournamentEventScheduleActivity = {
  id: string;
  scheduleDayId?: string;
  title: string;
  description?: string;
  startTime: string;
  endTime?: string;
  type: TournamentEventScheduleActivityType;
  customTypeName?: string;
  categoryKey?: string;
  categoryLabel?: string;
  locationLabel?: string;
  isHighlighted: boolean;
  displayOrder: number;
  isActive: boolean;
};

export type TournamentEventScheduleDay = {
  id: string;
  tournamentId?: string;
  date: string;
  title?: string;
  startTime?: string;
  endTime?: string;
  displayOrder: number;
  isActive: boolean;
  activities: TournamentEventScheduleActivity[];
};

export type TournamentEventScheduleWarning = {
  code: string;
  message: string;
  dayId?: string | null;
  activityId?: string | null;
};

export type TournamentEventSchedule = {
  isPublicEnabled: boolean;
  days: TournamentEventScheduleDay[];
  warnings?: TournamentEventScheduleWarning[];
};

export function getPublicTournamentBracket(
  id: number,
  categoryKey: string,
  token?: string
) {
  const authHeader =
    token && token.trim() ? { Authorization: `Bearer ${token.trim()}` } : {};
  return apiRequest<PublicTournamentBracket>(
    `/api/challenges/${encodeURIComponent(String(id))}/brackets/${encodeURIComponent(categoryKey)}`,
    {
      headers: authHeader,
    }
  );
}

export function getPublicTournamentSchedule(id: number, token?: string) {
  const authHeader =
    token && token.trim() ? { Authorization: `Bearer ${token.trim()}` } : {};
  return apiRequest<PublicTournamentSchedule>(
    `/api/challenges/${encodeURIComponent(String(id))}/schedule`,
    {
      headers: authHeader,
    }
  );
}

export function getPublicTournamentEventSchedule(id: number, token?: string) {
  const authHeader =
    token && token.trim() ? { Authorization: `Bearer ${token.trim()}` } : {};
  return apiRequest<TournamentEventSchedule>(
    `/api/challenges/${encodeURIComponent(String(id))}/event-schedule`,
    {
      headers: authHeader,
    }
  );
}

export function getAdminTournamentEventSchedule(token: string, id: number) {
  return apiRequest<TournamentEventSchedule>(
    `/api/admin/tournaments/${encodeURIComponent(String(id))}/event-schedule`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export function saveAdminTournamentEventSchedule(
  token: string,
  id: number,
  payload: TournamentEventSchedule,
) {
  return apiRequest<TournamentEventSchedule>(
    `/api/admin/tournaments/${encodeURIComponent(String(id))}/event-schedule`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    }
  );
}

export function createChallengeUpdatesEventSource(id: number) {
  const API_BASE = process.env.REACT_APP_API_BASE === "/" ? "" : (process.env.REACT_APP_API_BASE || "http://localhost:3333");
  return new EventSource(
    `${API_BASE}/api/challenges/${encodeURIComponent(String(id))}/stream`,
  );
}

export function listMyChallenges(token: string, filters?: ChallengeFilters) {
  return apiRequest<ChallengeHistoryItem[]>(
    `/api/me/challenges${buildQuery(filters)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export function upsertPlayerProfile(cpf: string, name: string) {
  return apiRequest<{ cpf: string; name: string }>("/api/players", {
    method: "POST",
    body: JSON.stringify({ cpf, name }),
  });
}

export function createPairForChallenge(
  challengeType: ChallengeType,
  cpf1: string,
  cpf2: string
) {
  return apiRequest<{ id: string; group: string; cpf1: string; cpf2: string }>(
    "/api/pairs",
    {
      method: "POST",
      body: JSON.stringify({
        group: challengeType,
        cpf1,
        cpf2,
      }),
    }
  );
}
